// "NEXT UP" — the reads behind the shell's Next up widget
// (HOME_AMBIENT_SKIN_SPEC.md §4, components/HomeNextUpBody.js).
//
// Data: `root.intranetEvents`, the calendar window the shell already loads
// for the "Coming up" widget (functions/loadPrivateData.js: one day back,
// fourteen ahead) — no new endpoint; `root.intranetEventsLoaded` says the
// window has answered. Every function here is registered, so a body reaches
// it as `el.call('homeNextUp…', …)` and stays closure-free.
//
// MINE: an event is the signed-in user's when its invitee list names them or
// they organised it. (The "Coming up" rule reads the invitee list only, so a
// meeting you organised without inviting yourself never showed there.) The
// feed repeats one booking under several ids; this dedupes by title + start +
// location, as "Coming up" does.
//
// PROVIDERS — the installed apps' own upcoming items (Nika, 2026-09-28: yes to
// "Next up with CRM viewings as the main item"; crm-w1's twin QA F3: the
// widget said "Nothing coming up" over 19 upcoming viewings). An installed
// app answers `homeUpcoming(ctx)` → Promise<Item[]>, a module function
// (Frank-discovered `functions/homeUpcoming.js`) reached through the app's
// OWN module context — the carrier the greeting's homeSignals uses
// (HOME_SIGNALS_CONTRACT v1, functions/homeSignals.js); backend reads inside a
// provider go through @symbo.ls/sdk.
//
//   Item = { id: '<app>.<kind>.<recordId>', kind: 'viewing' | …,
//            title,            // the words a person reads ("Viewing · Salome K.")
//            startAt, endAt?,  // ISO
//            place?,           // where, when the app knows it
//            href }            // an in-app route; Open takes it through openInSlot
//
//   ctx = { root (the app's state root), shell, sdk, workspaceId, lang, now,
//           limit: 5, app, appId }
//
// Rules: Next up is the signed-in person's agenda, so the APP decides whose
// items these are (its own "mine"); at most five, not ended at ctx.now; a
// provider has 5 s (a hang is cut and retried on the next pass); a throw
// reads as none and logs once per pass; one pass per minute per workspace,
// kept on the body's own state (`upcoming`) — no root key.

import { installedAppRefs } from './workspaceConfigs.js'

const _ms = (v) => {
  if (v == null || v === '') return NaN
  const t = new Date(v).getTime()
  return Number.isFinite(t) ? t : NaN
}
const _endMs = (e) => {
  const end = _ms(e && e.endAt)
  return Number.isFinite(end) ? end : _ms(e && e.startAt)
}
const _nextUpNow = (now) => (Number.isFinite(now) && now > 0 ? now : Date.now())
const HM = { hour: '2-digit', minute: '2-digit' }

// The user's events that have not ended at `now`, soonest start first — an
// event under way stays first until it ends.
export const homeNextUpEvents = function homeNextUpEvents(root, now) {
  const r = root || {}
  const me = r.currentUserEmail || ''
  const t = _nextUpNow(now)
  const seen = new Set()
  return (Array.isArray(r.intranetEvents) ? r.intranetEvents : [])
    .filter((e) => {
      if (!e || !me) return false
      if (!Number.isFinite(_ms(e.startAt)) || _endMs(e) <= t) return false
      const invited = Array.isArray(e.attendees) && e.attendees.includes(me)
      if (!invited && e.organizerEmail !== me) return false
      const key = (e.title || '') + '|' + (e.startAt || '') + '|' + (e.location || '')
      if (seen.has(key)) return false
      seen.add(key)
      return true
    })
    .sort((a, b) => _ms(a.startAt) - _ms(b.startAt))
}

// The hero of an event as three strings: the numeral (or the one word) and
// the words before and after it, cut from ONE bundle template per case, so
// every language keeps its own word order ("in 42 min" / "42 წუთში"). Under
// way → "now"; under an hour → minutes; under a day → hours; later → the
// weekday.
export const homeNextUpHero = function homeNextUpHero(event, now) {
  const start = _ms(event && event.startAt)
  if (!Number.isFinite(start)) return null
  const mins = Math.ceil((start - _nextUpNow(now)) / 60000)
  if (mins <= 0)
    return { value: this.call('polyglot', 'common.timeShort.now'), before: '', after: '' }
  let key = 'workspace.widget.nextUp.inHours'
  let n = Math.max(1, Math.round(mins / 60))
  if (mins < 60) {
    key = 'workspace.widget.nextUp.inMinutes'
    n = mins
  } else if (mins >= 24 * 60) {
    return {
      value: this.call('localizedDate', event.startAt, { weekday: 'long' }),
      before: '',
      after: ''
    }
  }
  const tpl = String(this.call('polyglot', key))
  const at = tpl.indexOf('{n}')
  if (at < 0) return { value: String(n), before: '', after: tpl }
  return { value: String(n), before: tpl.slice(0, at).trim(), after: tpl.slice(at + 3).trim() }
}

// "14:30 – 15:00 · Tbilisi": the event's times in this browser's zone and the
// zone's city (the IANA name's last part), so a teammate abroad reads whose
// clock it is.
export const homeNextUpTime = function homeNextUpTime(event) {
  if (!event || !event.startAt) return ''
  const from = this.call('localizedDate', event.startAt, HM)
  const to = event.endAt ? this.call('localizedDate', event.endAt, HM) : ''
  let zone = ''
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || ''
    zone = tz.split('/').pop().replace(/_/g, ' ')
  } catch (_) {
    zone = ''
  }
  const range = to && to !== from ? from + ' – ' + to : from
  return zone ? range + ' · ' + zone : range
}

// The items AFTER the next one, as list rows: the title, the start (with the
// short weekday when it is not today), the calendar hue mark, and the item's
// own record on a click — an event's detail sheet, an app item's route.
// `upcoming` is the body's provider pass (null: the calendar alone).
export const homeNextUpRows = function homeNextUpRows(root, now, upcoming) {
  const t = _nextUpNow(now)
  const midnight = new Date(t)
  midnight.setHours(24, 0, 0, 0)
  return homeNextUpItems
    .call(this, root, t, upcoming)
    .slice(1)
    .map((x) => {
      const hm = this.call('localizedDate', x.startAt, HM)
      const later = _ms(x.startAt) >= midnight.getTime()
      return {
        key: x.key,
        title: x.title,
        meta: later ? this.call('localizedDate', x.startAt, { weekday: 'short' }) + ' ' + hm : hm,
        mark: true,
        open: x.event
          ? { call: 'openEventDetailModal', arg: x.event }
          : { call: 'homeNextUpOpen', arg: x }
      }
    })
}

// The one action of the next item: an event gets Join when it has a meeting
// room or a call link, else Open (its detail sheet); an app item gets Open
// (its route). The label is a bundle key. Takes a merged item or a bare event.
export const homeNextUpAction = function homeNextUpAction(item) {
  if (!item) return null
  if (item.kind && item.kind !== 'event' && !item.event) {
    return item.href ? { label: 'common.action.open', call: 'homeNextUpOpen' } : null
  }
  const event = item.event || item
  if (this.call('eventHasMeetLink', event))
    return { label: 'workspace.rows.join', call: 'joinEventCall' }
  return { label: 'common.action.open', call: 'openEventDetailModal' }
}

// The action's argument: the bare event for the calendar calls, the item for
// an app item.
export const homeNextUpActionArg = function homeNextUpActionArg(item) {
  return item && item.event ? item.event : item
}

// Open an app item: its in-app route through the shell's ONE opener (slot vs
// page is openInSlot's decision). A merged calendar item opens its sheet.
export const homeNextUpOpen = function homeNextUpOpen(item) {
  if (!item) return
  if (item.event) return this.call('openEventDetailModal', item.event)
  if (item.href) return this.call('openInSlot', item.href, this)
}

// ── Providers ───────────────────────────────────────────────────────────────
// One app's answer → valid items: a title, a real start, not ended at `now`,
// an in-app href or none, soonest first, at most five.
export const homeNextUpNormalize = function homeNextUpNormalize(list, app, now) {
  if (!Array.isArray(list) || typeof app !== 'string' || !app) return []
  const t = _nextUpNow(now)
  const out = []
  const seen = new Set()
  for (const x of list) {
    if (!x || typeof x !== 'object') continue
    const title = typeof x.title === 'string' ? x.title.trim() : ''
    const start = _ms(x.startAt)
    if (!title || !Number.isFinite(start)) continue
    const end = _ms(x.endAt)
    if ((Number.isFinite(end) ? end : start) <= t) continue
    const key = typeof x.id === 'string' && x.id ? x.id : app + '.' + start + '.' + title
    if (seen.has(key)) continue
    seen.add(key)
    out.push({
      key,
      app,
      kind: typeof x.kind === 'string' && x.kind && x.kind !== 'event' ? x.kind : 'item',
      title,
      startAt: new Date(start).toISOString(),
      endAt: Number.isFinite(end) ? new Date(end).toISOString() : null,
      place: typeof x.place === 'string' ? x.place : '',
      href: typeof x.href === 'string' && /^\/(?!\/)/.test(x.href) ? x.href : null
    })
  }
  out.sort((a, b) => _ms(a.startAt) - _ms(b.startAt))
  return out.slice(0, 5)
}

// The installed apps that answer — the takeover module first, then the
// additive apps, each with its OWN `homeUpcoming` (the module context chains
// to the shell's merged functions, so an inherited name never counts). The
// provider runs as an element of ITS app: `this.call` resolves the app's
// functions first, `this.state.root` is its state root.
export const homeNextUpProviders = function homeNextUpProviders(root) {
  const el = this
  const r = root && typeof root === 'object' ? root : {}
  const out = []
  const seen = new Set()
  const apps = []
  const wp = r.workspaceProject
  if (wp && wp.id) apps.push(wp)
  for (const a of Array.isArray(r.workspaceApps) ? r.workspaceApps : []) apps.push(a)
  const getCtx = typeof r._moduleDsCtxGet === 'function' ? r._moduleDsCtxGet : null
  const getState = typeof r._moduleStateGet === 'function' ? r._moduleStateGet : null
  for (const entry of apps) {
    const appId = entry && entry.id ? String(entry.id) : ''
    if (!appId || seen.has(appId)) continue
    seen.add(appId)
    // Still SETTING UP (`pendingWidgets` an array on its published entry) or
    // its setup FAILED (`setupFailed: true`): no state root to read, so it is
    // never asked — homeNextUpAppsKnown keeps "nothing" back when it feeds
    // Next up.
    if (Array.isArray(entry.pendingWidgets) || entry.setupFailed === true) continue
    const modCtx = getCtx ? getCtx(appId) : null
    const fns = modCtx && modCtx.functions
    const own =
      fns && Object.prototype.hasOwnProperty.call(fns, 'homeUpcoming') ? fns.homeUpcoming : null
    if (typeof own !== 'function') continue
    const app = appId.split('/').pop().replace(/-app$/, '')
    const stateRoot = (getState && getState(appId)) || r
    const shim = {
      key: 'homeUpcoming',
      context: modCtx,
      sdk: (el && el.sdk) || null,
      state: { root: stateRoot },
      call: (name, ...args) => {
        const f = modCtx.functions[name]
        return typeof f === 'function' ? f.apply(shim, args) : undefined
      }
    }
    out.push({
      app,
      appId,
      run: (ctx) => own.call(shim, { ...ctx, app, appId, root: stateRoot, shell: r })
    })
  }
  return out
}

// Are the workspace's installed apps KNOWN yet? Nobody can say "nothing" before
// every provider is known (the rule homeSignals follows): a workspace that
// declares apps has not published `workspaceApps` on a cold load, and a
// takeover module has not published `workspaceProject` (crm-w1's live check:
// the first pass ran before the CRM registered and the card said "Nothing
// coming up" for a minute).
export const homeNextUpAppsKnown = function homeNextUpAppsKnown(root) {
  const r = root && typeof root === 'object' ? root : {}
  // The workspace RECORD first (homeSignals.js homeSignalsProviders, the same
  // rule — both readers answer one question): before the full record arrives
  // its settings bag is absent and the installed-apps list reads [] — not
  // "no apps". Loaded = the settings bag or the full-record stamp; while an id
  // is known and the completion is in flight, NOT known; a completion that
  // definitively failed (`_completionUnreachable`) is terminal.
  const w = r.workspace
  const settingsReady =
    !!w && (w._shape === 'full' || (!!w.settings && typeof w.settings === 'object'))
  if (!settingsReady && !!w && !!(w.id || w._id) && !w._completionUnreachable) return false
  const settings = (settingsReady && w.settings) || {}
  // Installed APP entries only (the loader's rule, workspaceConfigs.js
  // installedAppRefs): a records-grant row never loads, so it never publishes.
  const appsKnown = !installedAppRefs(settings).length || Array.isArray(r.workspaceApps)
  const moduleKnown = !settings.workspaceModule || !!r.workspaceProject
  // An app still setting up (`pendingWidgets` an array) or whose setup failed
  // (`setupFailed: true`) has not answered — and a failed one never will, so
  // the card never says "nothing" over it. It holds Next up back only when it
  // FEEDS Next up: `homeProviders` (published by the loader at the first apps
  // pass) names 'upcoming'; [] or a list without it never blocks. An entry
  // without the field (an older loader) keeps the wait.
  const published = [r.workspaceProject].concat(
    Array.isArray(r.workspaceApps) ? r.workspaceApps : []
  )
  const holding = published.some((e) => {
    if (!e || !(Array.isArray(e.pendingWidgets) || e.setupFailed === true)) return false
    const feeds = e.homeProviders
    return !Array.isArray(feeds) || feeds.includes('upcoming')
  })
  return appsKnown && moduleKnown && !holding
}

// The provider set as one string — a pass answers for THIS set; a provider
// that registers later changes it, and the pass runs again inside its TTL.
export const homeNextUpProviderSig = function homeNextUpProviderSig(root) {
  const el = this
  const list = el && typeof el.call === 'function' ? el.call('homeNextUpProviders', root) || [] : []
  return list
    .map((p) => p.appId)
    .sort()
    .join('|')
}

// One provider pass for the body `this`: every provider at once, each cut at
// the budget (5 s), the answers normalized and written to the body's own
// `upcoming` ({ ws, at, items, answered }). A pass younger than a minute for
// this workspace is kept; a late answer for a workspace the person left is
// dropped. `opts` are test seams: { now, budget, ttl }.
export const homeNextUpCollect = async function homeNextUpCollect(opts) {
  const el = this
  const o = opts && typeof opts === 'object' ? opts : {}
  const s = el && el.state
  const r = s && s.root
  if (!s || !r || typeof s.update !== 'function') return null
  const wsOf = () => (r.workspace && String(r.workspace.id || r.workspace._id || '')) || null
  const ws = wsOf()
  if (!ws) return null
  const now = typeof o.now === 'number' ? o.now : Date.now()
  const TTL = typeof o.ttl === 'number' ? o.ttl : 60000
  const BUDGET = typeof o.budget === 'number' ? o.budget : 5000
  let providers = []
  try {
    providers = el.call('homeNextUpProviders', r) || []
  } catch (err) {
    console.warn('[home] next-up providers unavailable:', err && err.message)
  }
  const sig = providers
    .map((p) => p.appId)
    .sort()
    .join('|')
  const cur = s.upcoming
  // A pass is kept for a minute — for the SAME provider set: a provider that
  // registers after it (a cold load publishes the apps later) runs at once.
  if (cur && cur.ws === ws && cur.sig === sig && now - cur.at < TTL) return cur
  if (el.__homeNextUpPass) return cur || null
  el.__homeNextUpPass = true
  try {
    const ctx = { sdk: el.sdk || null, workspaceId: ws, lang: r.lang || 'en', now, limit: 5 }
    // The budget timer is CLEARED when its race settles — never `unref`'d. An
    // unref'd timer cannot hold the loop open, so when a hung provider is the
    // last work on it the loop drains, the budget never fires, the race never
    // settles and the whole pass is left pending. Under `node --test` that
    // reads as "Promise resolution is still pending but the event loop has
    // already resolved", and it cancelled four tests on node v22.23.4 while
    // node v24.22.1 happened to hide it. Clearing gives the budget the one
    // thing unref took away — the ability to fire — and still leaves no handle
    // behind once the provider answered.
    const cut = () => {
      let timer
      const promise = new Promise((resolve) => {
        timer = setTimeout(() => resolve({ hung: true }), BUDGET)
      })
      return { promise, clear: () => clearTimeout(timer) }
    }
    const answers = await Promise.all(
      providers.map((p) => {
        const budget = cut()
        return Promise.race([
          Promise.resolve()
            .then(() => p.run(ctx))
            .then(
              (list) => ({ list }),
              (err) => ({ err })
            ),
          budget.promise
        ])
          .then((a) => ({ app: p.app, ...a }))
          .finally(budget.clear)
      })
    )
    const items = []
    for (const a of answers) {
      if (a.err)
        console.warn(
          '[home] next-up provider failed — read as none:',
          a.app,
          a.err && a.err.message
        )
      else if (a.hung) console.warn('[home] next-up provider did not answer in time:', a.app)
      if (a.list) items.push(...el.call('homeNextUpNormalize', a.list, a.app, now))
    }
    if (wsOf() !== ws) return null
    const next = { ws, sig, at: now, items, answered: true }
    s.update({ upcoming: next }, { preventFetch: true })
    return next
  } finally {
    el.__homeNextUpPass = false
  }
}

// Everything next, soonest first: the calendar window (the user's events)
// and the providers' items that have not ended at `now`. One shape for the
// body: { key, kind, title, startAt, endAt, event? (a calendar event), href?,
// place? }.
export const homeNextUpItems = function homeNextUpItems(root, now, upcoming) {
  const t = _nextUpNow(now)
  const events = homeNextUpEvents.call(this, root, t).map((e, i) => ({
    key: String(e.id != null ? e.id : 'e' + i),
    kind: 'event',
    title: e.title || '',
    startAt: e.startAt,
    endAt: e.endAt || null,
    event: e
  }))
  const items = (upcoming && Array.isArray(upcoming.items) ? upcoming.items : []).filter((x) => {
    const end = _ms(x && x.endAt)
    return x && (Number.isFinite(end) ? end : _ms(x.startAt)) > t
  })
  return [...events, ...items].sort((a, b) => _ms(a.startAt) - _ms(b.startAt))
}

// The widget's minute clock: the body's own `now` moves every 30 s, so "in
// 42 min" counts down and a finished event leaves; the providers' pass
// refreshes on the same clock, once a minute. One timer per body; it
// stops when the body leaves the page. A background tab throttles it to once
// a minute, which is all a minute clock needs.
export const homeNextUpClock = function homeNextUpClock() {
  const el = this
  if (!el || el.__homeNextUpClock || typeof setInterval !== 'function') return
  const iv = setInterval(() => {
    if (!el.node || !el.state || typeof el.state.update !== 'function') {
      clearInterval(iv)
      el.__homeNextUpClock = null
      return
    }
    el.state.update({ now: Date.now() }, { preventFetch: true })
    // The providers' pass keeps its own minute (homeNextUpCollect's TTL).
    el.call('homeNextUpCollect')
  }, 30000)
  // Node (the jsdom tests) must not wait on the clock to exit.
  if (iv && typeof iv.unref === 'function') iv.unref()
  el.__homeNextUpClock = iv
}

export const homeNextUpClockStop = function homeNextUpClockStop() {
  const el = this
  if (el && el.__homeNextUpClock) {
    clearInterval(el.__homeNextUpClock)
    el.__homeNextUpClock = null
  }
}
